import {React, observer, computed, useLocalStore, useAsObservableSource, useEffect} from '@api'
import {ArrowShape, DashStyle, EASINGS, GHOSTED_OPACITY, TldrawApp, TldrawHandle, useTldrawApp} from '@modules/whiteboard'
import {getArcLength, ArrowUtil, getCtp, getCurvedArrowHeadPoints, getStraightArrowHeadPoints} from './'
import {SVGContainer, TLComponentProps, Utils} from '@tldraw/core'
import {Vec} from '@tldraw/vec'
import {ShapeComponentStore} from '@modules/whiteboard/shapes/ShapeComponentStore'
import {getStroke} from 'perfect-freehand'

interface MyProps extends TLComponentProps<ArrowShape> {

}

/// region Store

export class ArrowShapeComponentStore extends ShapeComponentStore<ArrowShape, ArrowUtil> {
  constructor(public props: MyProps & {app: TldrawApp}) {
    super(props)
  }

  @computed get isDraw() { return this.shape.style.dash === DashStyle.Draw}

  @computed get isStraightLine() {
    const {shape: {handles: {bend, start, end}}} = this
    return Vec.dist(bend.point, Vec.toFixed(Vec.med(start.point, end.point))) < 1
  }

  @computed get arrowDist() {
    const {shape: {handles: {start, end}}} = this
    return Vec.dist(start.point, end.point)
  }

  @computed get arrowHeadLength() {
    const {arrowDist, shapeStyle: {strokeWidth}} = this
    return Math.min(arrowDist / 3, strokeWidth * 8)
  }

  @computed get adjustedStrokeWidth() {
    const {shapeStyle: {strokeWidth}} = this
    return 1 + strokeWidth * 1.618
  }

  @computed get circle() {
    const {isStraightLine, shape} = this

    return isStraightLine ? null : getCtp(shape)
  }

  @computed get path() {
    const {isStraightLine, isDraw, shape: {handles: {start, end}}} = this
    if (isStraightLine) {
      return isDraw
             ? this.freehandArrowShaft
             : 'M' + Vec.toFixed(start.point) + 'L' + Vec.toFixed(end.point)
    } else {
      return isDraw
             ? this.curvedFreehandArrowShaft
             : this.arrowArcPath
    }
  }

  @computed get arrowArcPath() {
    const {shape: {handles: {start, end}, bend}, circle} = this

    return [
      'M',
      start.point[0],
      start.point[1],
      'A',
      circle[2],
      circle[2],
      0,
      0,
      bend < 0 ? 0 : 1,
      end.point[0],
      end.point[1],
    ].join(' ')
  }


  @computed get arrowArc() {
    const { shape: {handles: {start, end, bend }}} = this,
      [cx, cy, radius] = Utils.circleFromThreePoints(start.point, end.point, bend.point),
      center = [cx, cy],
      length = getArcLength(center, radius, start.point, end.point)

    return { center, radius, length }
  }

  @computed get curvedFreehandArrowShaft() {
    const {arrowArc: {length}, easing, circle, shape: {id, handles: {start, end}, decorations}, shapeStyle: {strokeWidth}} = this

    const getRandom = Utils.rng(id)

    const center = [circle[0], circle[1]],
      radius = circle[2]

    const startPoint = decorations?.start
                       ? Vec.rotWith(start.point, center, strokeWidth / length)
                       : start.point,
      endPoint = decorations?.end
                     ? Vec.rotWith(end.point, center, -(strokeWidth / length))
                     : end.point

    const startAngle = Vec.angle(center, startPoint),
      endAngle = Vec.angle(center, endPoint)

    const points: number[][] = [],
      count = 8 + Math.floor((Math.abs(length) / 20) * 1 + getRandom() / 2)

    for (let i = 0; i < count; i++) {
      const t = easing(i / count)

      const angle = Utils.lerpAngles(startAngle, endAngle, t)

      points.push(Vec.toFixed(Vec.nudgeAtAngle(center, angle, radius)))
    }

    const stroke = getStroke([startPoint, ...points, endPoint], {
        size: 1 + strokeWidth,
        thinning: 0.618 + getRandom() * 0.2,
        easing: EASINGS.easeOutQuad,
        simulatePressure: false,
        streamline: 0,
        last: true,
      }),
      path = Utils.getSvgPathFromStroke(stroke)

    return path
  }

  @computed get easing() {
    const {shape} = this
    const getRandom = Utils.rng(shape.id)

    return EASINGS[getRandom() > 0 ? 'easeInOutSine' : 'easeInOutCubic']
  }

  @computed get startArrowHead() {
    const {shape: {decorations, handles: {start, end}}, isStraightLine, arrowHeadLength, arrowArc} = this

    if (decorations.start) {
      return isStraightLine
             ? getStraightArrowHeadPoints(start.point, end.point, arrowHeadLength)
             : getCurvedArrowHeadPoints(start.point, arrowHeadLength, arrowArc.center, arrowArc.radius, arrowArc.length < 0)
    }

    return null
  }

  @computed get endArrowHead() {
    const {shape: {decorations, handles: {start, end}}, isStraightLine, arrowHeadLength, arrowArc} = this

    if (decorations.end) {
      return isStraightLine
             ? getStraightArrowHeadPoints(end.point, start.point, arrowHeadLength)
             : getCurvedArrowHeadPoints(end.point, arrowHeadLength, arrowArc.center, arrowArc.radius, arrowArc.length >= 0
        )
    }

    return null
  }

  @computed get freehandArrowShaft() {
    const {shapeStyle: {strokeWidth}, shape: {id, decorations, handles: {start, end}}} = this,
      getRandom = Utils.rng(id)

    const startPoint = decorations?.start
                       ? Vec.nudge(start.point, end.point, strokeWidth)
                       : start.point,
      endPoint = decorations?.end
                 ? Vec.nudge(end.point, start.point, strokeWidth)
                 : end.point

    const stroke = getStroke([startPoint, endPoint], {
        size: strokeWidth,
        thinning: 0.618 + getRandom() * 0.2,
        easing: EASINGS.easeOutQuad,
        simulatePressure: true,
        streamline: 0,
        last: true,
      }),
      path = Utils.getSvgPathFromStroke(stroke)

    return path
  }
}

export const ArrowShapeComponentStoreContext = React.createContext<ArrowShapeComponentStore>(null)
export const useArrowShapeComponentStore = () => React.useContext(ArrowShapeComponentStoreContext)

/// endregion

/// region Components

export const ArrowShapeComponent = observer<MyProps, any>(function ArrowShapeComponent(props, ref) {
  const app = useTldrawApp(),
    obsProps = useAsObservableSource({...props, app}),
    {
      shapeStyle: {strokeWidth, strokeColor: stroke}, adjustedStrokeWidth,
      shape: {style: {dash}, handles: {start, end}},
      path, isStraightLine, isDraw, arrowDist, startArrowHead, endArrowHead,
      id, props: {events, isGhost}, registerEffects
    } = useLocalStore(() => new ArrowShapeComponentStore(obsProps))

  useEffect(() => registerEffects(), [])

  let shaftPath: JSX.Element | null

  if (isStraightLine) {
    const {strokeDasharray, strokeDashoffset} = Utils.getPerfectDashProps(
      arrowDist,
      strokeWidth * 1.618,
      dash,
      2,
      false
    )

    // Straight arrow path
    shaftPath =
      arrowDist > 2 ? (
        <>
          <path
            d={path}
            fill='none'
            strokeWidth={Math.max(8, strokeWidth * 2)}
            strokeDasharray='none'
            strokeDashoffset='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            pointerEvents='stroke'
          />
          <path
            d={path}
            fill={stroke}
            stroke={stroke}
            strokeWidth={isDraw ? adjustedStrokeWidth / 2 : adjustedStrokeWidth}
            strokeDasharray={strokeDasharray}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap='round'
            strokeLinejoin='round'
            pointerEvents='stroke'
          />
        </>
      ) : null
  } else {

    const {strokeDasharray, strokeDashoffset} = Utils.getPerfectDashProps(
      Math.abs(length),
      adjustedStrokeWidth,
      dash,
      2,
      false
    )

    // Curved arrow path
    shaftPath = (
      <>
        <path
          d={path}
          fill='none'
          stroke='none'
          strokeWidth={Math.max(8, strokeWidth)}
          strokeDasharray='none'
          strokeDashoffset='none'
          strokeLinecap='round'
          strokeLinejoin='round'
          pointerEvents='stroke'
        />
        <path
          d={path}
          fill={isDraw ? stroke : 'none'}
          stroke={stroke}
          strokeWidth={isDraw ? 0 : adjustedStrokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap='round'
          strokeLinejoin='round'
          pointerEvents='stroke'
        />
      </>
    )
  }

  return (
    <SVGContainer ref={ref} id={id} {...events}>
      <g pointerEvents='none' opacity={isGhost ? GHOSTED_OPACITY : 1}>
        {shaftPath}
        {startArrowHead && (
          <path
            d={`M ${startArrowHead.left} L ${start.point} ${startArrowHead.right}`}
            fill='none'
            stroke={stroke}
            strokeWidth={adjustedStrokeWidth}
            strokeDashoffset='none'
            strokeDasharray='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            pointerEvents='stroke'
          />
        )}
        {endArrowHead && (
          <path
            d={`M ${endArrowHead.left} L ${end.point} ${endArrowHead.right}`}
            fill='none'
            stroke={stroke}
            strokeWidth={adjustedStrokeWidth}
            strokeDashoffset='none'
            strokeDasharray='none'
            strokeLinecap='round'
            strokeLinejoin='round'
            pointerEvents='stroke'
          />
        )}
      </g>
    </SVGContainer>
  )
}, {forwardRef: true})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/whiteboard/shapes/arrow/ArrowShapeComponent.tsx